/**
 * Hook for tracking user location during an active adventure session.
 * Sends periodic location updates to the session API for condition evaluation.
 */

import { useEffect, useRef, useCallback } from 'react'
import { sessionsApi } from '@api/endpoints/sessions'
import { SessionStatus } from '@api/types/session'

const BASE_INTERVAL_MS = 15000 // 15 seconds - base interval
const MAX_INTERVAL_MS = 300000 // 5 minutes - max backoff interval
const BACKOFF_MULTIPLIER = 2 // Double the interval on each 429

interface UseSessionLocationTrackingOptions {
  sessionId: string | null
  sessionStatus: SessionStatus | null
  enabled?: boolean
}

export function useSessionLocationTracking({
  sessionId,
  sessionStatus,
  enabled = true,
}: UseSessionLocationTrackingOptions) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isTrackingRef = useRef(false)
  const lastPositionRef = useRef<{ lat: number; lng: number } | null>(null)
  const currentIntervalRef = useRef(BASE_INTERVAL_MS)
  const consecutiveErrorsRef = useRef(0)

  // Check if session is active and should track location
  const shouldTrack = enabled &&
    !!sessionId &&
    sessionStatus === SessionStatus.InProgress

  // Restart interval with new duration
  const restartInterval = useCallback((newInterval: number) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    currentIntervalRef.current = newInterval
    // updateLocation will be defined below, we'll reference it via a stable ref
    intervalRef.current = setInterval(() => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => sendLocationUpdateRef.current?.(pos),
          (err) => console.error('[SessionLocation] Geolocation error:', err.message),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        )
      }
    }, newInterval)
    console.log(`[SessionLocation] Interval changed to ${newInterval / 1000}s`)
  }, [])

  // Ref to hold the current sendLocationUpdate function
  const sendLocationUpdateRef = useRef<((pos: GeolocationPosition) => void) | null>(null)

  // Send location update to session API
  const sendLocationUpdate = useCallback(async (position: GeolocationPosition) => {
    if (!sessionId) return

    const newLat = position.coords.latitude
    const newLng = position.coords.longitude

    // Only send if position has changed significantly (more than ~10 meters)
    const lastPos = lastPositionRef.current
    if (lastPos) {
      const latDiff = Math.abs(newLat - lastPos.lat)
      const lngDiff = Math.abs(newLng - lastPos.lng)
      // ~0.0001 degrees is roughly 10 meters
      if (latDiff < 0.0001 && lngDiff < 0.0001) {
        console.log('[SessionLocation] Position unchanged, skipping update')
        return
      }
    }

    try {
      await sessionsApi.updateLocation(sessionId, {
        latitude: newLat,
        longitude: newLng,
        accuracy: position.coords.accuracy,
      })
      lastPositionRef.current = { lat: newLat, lng: newLng }
      consecutiveErrorsRef.current = 0

      // Reset to base interval on success if we were backed off
      if (currentIntervalRef.current > BASE_INTERVAL_MS) {
        console.log('[SessionLocation] Request succeeded, resetting to base interval')
        restartInterval(BASE_INTERVAL_MS)
      }

      console.log('[SessionLocation] Location updated:', {
        lat: newLat.toFixed(6),
        lng: newLng.toFixed(6),
        accuracy: Math.round(position.coords.accuracy),
      })
    } catch (error: any) {
      // Check for 429 Too Many Requests
      const status = error?.response?.status || error?.status
      if (status === 429) {
        consecutiveErrorsRef.current++
        const newInterval = Math.min(
          currentIntervalRef.current * BACKOFF_MULTIPLIER,
          MAX_INTERVAL_MS
        )
        console.warn(`[SessionLocation] Rate limited (429). Backing off to ${newInterval / 1000}s`)
        restartInterval(newInterval)
      } else {
        console.error('[SessionLocation] Failed to update location:', error)
      }
    }
  }, [sessionId, restartInterval])

  // Keep ref updated
  sendLocationUpdateRef.current = sendLocationUpdate

  // Get current position and send update
  const updateLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn('[SessionLocation] Geolocation not supported')
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        sendLocationUpdate(position)
      },
      (error) => {
        console.error('[SessionLocation] Geolocation error:', error.message)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000,
      }
    )
  }, [sendLocationUpdate])

  // Start/stop location tracking based on session state
  useEffect(() => {
    if (shouldTrack && !isTrackingRef.current) {
      console.log('[SessionLocation] Starting location tracking for session:', sessionId)
      isTrackingRef.current = true
      lastPositionRef.current = null
      currentIntervalRef.current = BASE_INTERVAL_MS // Reset to base interval on start
      consecutiveErrorsRef.current = 0

      // Initial update
      updateLocation()

      // Periodic updates
      intervalRef.current = setInterval(updateLocation, BASE_INTERVAL_MS)
    } else if (!shouldTrack && isTrackingRef.current) {
      console.log('[SessionLocation] Stopping location tracking')
      isTrackingRef.current = false

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      isTrackingRef.current = false
    }
  }, [shouldTrack, sessionId, updateLocation])

  // Manual trigger for immediate location update
  const triggerUpdate = useCallback(() => {
    if (shouldTrack) {
      updateLocation()
    }
  }, [shouldTrack, updateLocation])

  return {
    isTracking: isTrackingRef.current,
    triggerUpdate,
  }
}
